import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
  Tooltip,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  CloudUpload,
  Info,
  Image,
  Delete,
  Language,
  Email,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isAdmin } from '../../utils/auth';
import api from '../../services/api';
import RequireEmailVerification from '../../components/auth/RequireEmailVerification';
import BoardMembersManager from './boardMembers/BoardMembersManager';
import ClubDocumentsManager from './clubDocuments/ClubDocumentsManager';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ConfigureClub: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clubId } = useParams<{ clubId: string }>();
  const user = useSelector((state: RootState) => state.auth.user);
  const isPlatformAdmin = isAdmin();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Club details state
  const [clubName, setClubName] = useState('');
  const [description, setDescription] = useState('');
  const [fee, setFee] = useState<number>(0);
  const [paymentInfo, setPaymentInfo] = useState('');
  const [nationalFederationEmail, setNationalFederationEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [clubEmail, setClubEmail] = useState('');

  // Logo state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploaded, setLogoUploaded] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);
  const [hasLogo, setHasLogo] = useState(false);

  // Card template state
  const [frontPosition, setFrontPosition] = useState('');
  const [frontCardFile, setFrontCardFile] = useState<File | null>(null);
  const [backCardFile, setBackCardFile] = useState<File | null>(null);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [frontUploaded, setFrontUploaded] = useState(false);
  const [backUploaded, setBackUploaded] = useState(false);
  const [frontPreviewUrl, setFrontPreviewUrl] = useState<string | null>(null);
  const [backPreviewUrl, setBackPreviewUrl] = useState<string | null>(null);
  const [currentFrontUrl, setCurrentFrontUrl] = useState<string | null>(null);
  const [currentBackUrl, setCurrentBackUrl] = useState<string | null>(null);

  useEffect(() => {
    if (clubId) {
      fetchClubData();
      fetchCurrentCardImages();
      fetchCurrentLogo();
    }
  }, [clubId]);

  useEffect(() => {
    return () => {
      // Clean up all object URLs when component unmounts
      if (frontPreviewUrl) {
        URL.revokeObjectURL(frontPreviewUrl);
      }
      if (backPreviewUrl) {
        URL.revokeObjectURL(backPreviewUrl);
      }
      if (currentFrontUrl) {
        URL.revokeObjectURL(currentFrontUrl);
      }
      if (currentBackUrl) {
        URL.revokeObjectURL(currentBackUrl);
      }
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
      if (currentLogoUrl) {
        URL.revokeObjectURL(currentLogoUrl);
      }
    };
    // Only run cleanup on unmount, not on every URL change
  }, []);

  const fetchClubData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/clubs/${clubId}`);
      const club = response.data;
      
      setClubName(club.club_name || '');
      setDescription(club.description || '');
      setFee(club.fee || 0);
      setPaymentInfo(club.payment_info || '');
      setNationalFederationEmail(club.national_federation_email || '');
      setFrontPosition(club.front_position || '');
      setWebsiteUrl(club.website_url || '');
      setClubEmail(club.club_email || '');
      setHasLogo(!!club.logo_path);
    } catch (err: any) {
      setError(t('common.errors.failedToLoad'));
      console.error('Failed to fetch club:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentCardImages = async () => {
    if (!clubId) return;
    
    // Clean up old URLs before creating new ones
    if (currentFrontUrl) {
      URL.revokeObjectURL(currentFrontUrl);
      setCurrentFrontUrl(null);
    }
    if (currentBackUrl) {
      URL.revokeObjectURL(currentBackUrl);
      setCurrentBackUrl(null);
    }
    
    try {
      const frontResponse = await api.get(`/club-template/${clubId}/front`, { 
        responseType: 'blob' 
      });
      if (frontResponse.data) {
        const frontUrl = URL.createObjectURL(frontResponse.data);
        setCurrentFrontUrl(frontUrl);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('No front template uploaded yet');
        setCurrentFrontUrl(null);
      } else {
        console.error('Error fetching front template:', error.response?.status, error.message);
      }
    }

    try {
      const backResponse = await api.get(`/club-template/${clubId}/back`, { 
        responseType: 'blob' 
      });
      if (backResponse.data) {
        const backUrl = URL.createObjectURL(backResponse.data);
        setCurrentBackUrl(backUrl);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('No back template uploaded yet');
        setCurrentBackUrl(null);
      } else {
        console.error('Error fetching back template:', error.response?.status, error.message);
      }
    }
  };

  const fetchCurrentLogo = async () => {
    if (!clubId) return;

    // Clean up old URL before creating new one
    if (currentLogoUrl) {
      URL.revokeObjectURL(currentLogoUrl);
      setCurrentLogoUrl(null);
    }

    try {
      const logoResponse = await api.get(`/clubs/${clubId}/logo`, {
        responseType: 'blob'
      });
      if (logoResponse.data) {
        const logoUrl = URL.createObjectURL(logoResponse.data);
        setCurrentLogoUrl(logoUrl);
        setHasLogo(true);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('No logo uploaded yet');
        setCurrentLogoUrl(null);
        setHasLogo(false);
      } else {
        console.error('Error fetching logo:', error.response?.status, error.message);
      }
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    const validFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const maxFileSize = 5 * 1024 * 1024; // 5MB

    if (!validFileTypes.includes(logoFile.type)) {
      setError(t('clubs.configure.basicInfo.logo.invalidFileType'));
      return;
    }

    if (logoFile.size > maxFileSize) {
      setError(t('clubs.configure.basicInfo.logo.fileTooLarge'));
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', logoFile);

      await api.post(`/clubs/${clubId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(t('clubs.configure.basicInfo.logo.uploadSuccess'));
      setLogoUploaded(true);
      setLogoFile(null);
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
        setLogoPreviewUrl(null);
      }

      // Refresh logo after a short delay
      setTimeout(() => {
        fetchCurrentLogo();
        setSuccess(null);
      }, 1000);
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError(t('clubs.configure.basicInfo.logo.uploadError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setUploadingLogo(true);
      await api.delete(`/clubs/${clubId}/logo`);
      setSuccess(t('clubs.configure.basicInfo.logo.deleteSuccess'));
      setHasLogo(false);
      if (currentLogoUrl) {
        URL.revokeObjectURL(currentLogoUrl);
        setCurrentLogoUrl(null);
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting logo:', err);
      setError(t('clubs.configure.basicInfo.logo.deleteError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveClubDetails = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updates = {
        club_name: clubName,
        description: description,
        fee: fee,
        payment_info: paymentInfo,
        national_federation_email: nationalFederationEmail,
        website_url: websiteUrl || null,
        club_email: clubEmail || null,
      };

      await api.put(`/clubs/${clubId}`, updates);

      setSuccess(t('clubs.configure.basicInfo.updateSuccess'));
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Failed to update club:', err);
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Handle FastAPI validation errors
        const firstError = errorDetail[0];
        setError(firstError.msg || t('common.errors.updateFailed'));
      } else {
        setError(t('common.errors.updateFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFrontPosition = async () => {
    const isValidFormat = /^\d+,\d+$/.test(frontPosition);
    
    if (!isValidFormat) {
      setError(t('clubs.configure.templates.cardPosition.invalidFormat'));
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      await api.put(`/clubs/${clubId}`, { front_position: frontPosition });
      setSuccess(t('clubs.configure.templates.cardPosition.saved'));
      setSnackbarOpen(true);
    } catch (err: any) {
      setError(t('common.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCardUpload = async (type: 'front' | 'back') => {
    const file = type === 'front' ? frontCardFile : backCardFile;
    if (!file) return;

    const validFileTypes = ['image/png'];
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    
    if (!validFileTypes.includes(file.type)) {
      setError(t('clubs.configure.templates.invalidFileType'));
      return;
    }
    
    if (file.size > maxFileSize) {
      setError(t('clubs.configure.templates.fileTooLarge'));
      return;
    }
    
    try {
      if (type === 'front') {
        setUploadingFront(true);
      } else {
        setUploadingBack(true);
      }
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/upload-card-template-minio/${clubId}?image_type=${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setSuccess(t('clubs.configure.templates.uploadSuccess'));
      
      if (type === 'front') {
        setFrontUploaded(true);
        setFrontCardFile(null);
        if (frontPreviewUrl) {
          URL.revokeObjectURL(frontPreviewUrl);
          setFrontPreviewUrl(null);
        }
      } else {
        setBackUploaded(true);
        setBackCardFile(null);
        if (backPreviewUrl) {
          URL.revokeObjectURL(backPreviewUrl);
          setBackPreviewUrl(null);
        }
      }
      
      // Refresh images after a short delay to ensure server has processed
      setTimeout(() => {
        fetchCurrentCardImages();
        setSuccess(null);
      }, 1000);
    } catch (err: any) {
      console.error('Error:', err);
      setError(t('common.errors.uploadError'));
    } finally {
      if (type === 'front') {
        setUploadingFront(false);
      } else {
        setUploadingBack(false);
      }
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <RequireEmailVerification>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/app/admin/clubs')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            {t('clubs.configure.title')}: {clubName || t('common.loading')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label={t('clubs.configure.tabs.basicInfo')} />
            <Tab label={t('clubs.configure.tabs.cardTemplate')} />
            <Tab label={t('clubs.configure.tabs.boardMembers')} />
            <Tab label={t('clubs.configure.tabs.documents')} />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('clubs.configure.basicInfo.title')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('clubs.configure.basicInfo.clubName')}
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                required
              />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('clubs.configure.basicInfo.description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              
              <TextField
                fullWidth
                label={t('clubs.configure.basicInfo.registrationFee', { defaultValue: 'Registration Fee (€)' })}
                type="number"
                value={fee}
                onChange={(e) => setFee(parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={t('clubs.configure.basicInfo.registrationFeeHelper', { defaultValue: 'Set the registration fee for this club (0 for free)' })}
                sx={{ maxWidth: 400 }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('clubs.configure.basicInfo.paymentInfo', { defaultValue: 'Payment Instructions' })}
                value={paymentInfo}
                onChange={(e) => setPaymentInfo(e.target.value)}
                helperText={t('clubs.configure.basicInfo.paymentInfoHelper', { defaultValue: 'Enter bank account details (IBAN, BIC, etc.) or specify "Cash only". This will be shown to users during registration.' })}
                placeholder={t('clubs.configure.basicInfo.paymentInfoPlaceholder', { defaultValue: 'Example:\nBank: ABC Bank\nIBAN: IT12 3456 7890 1234 5678 9012 345\nBIC/SWIFT: ABCDIT2AXXX\nAccount holder: Club Name\n\nOr simply: Cash payment only at club office' })}
              />
              
              <TextField
                fullWidth
                label={t('clubs.configure.basicInfo.nationalFederationEmail')}
                type="email"
                value={nationalFederationEmail}
                onChange={(e) => setNationalFederationEmail(e.target.value)}
                helperText={t('clubs.configure.basicInfo.nationalFederationEmailHelper')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={t('clubs.configure.basicInfo.nationalFederationEmailTooltip')}
                        arrow
                      >
                        <Info sx={{ color: 'action.active', cursor: 'help' }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold">
                {t('clubs.configure.basicInfo.publicInfo.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('clubs.configure.basicInfo.publicInfo.description')}
              </Typography>

              <TextField
                fullWidth
                label={t('clubs.configure.basicInfo.publicInfo.websiteUrl')}
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://www.example.com"
                helperText={t('clubs.configure.basicInfo.publicInfo.websiteUrlHelper')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Language color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type="email"
                label={t('clubs.configure.basicInfo.publicInfo.clubEmail')}
                value={clubEmail}
                onChange={(e) => setClubEmail(e.target.value)}
                placeholder="info@club.com"
                helperText={t('clubs.configure.basicInfo.publicInfo.clubEmailHelper')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Club Logo Upload */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('clubs.configure.basicInfo.logo.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('clubs.configure.basicInfo.logo.description')}
                </Typography>

                {(currentLogoUrl || logoPreviewUrl) && (
                  <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: logoPreviewUrl ? 'primary.main' : 'divider', borderRadius: 1, display: 'inline-block' }}>
                    <img
                      src={logoPreviewUrl || currentLogoUrl || ''}
                      alt="Club Logo"
                      style={{ maxWidth: 200, maxHeight: 200, display: 'block' }}
                    />
                    {!logoPreviewUrl && hasLogo && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleDeleteLogo}
                        disabled={uploadingLogo}
                        sx={{ mt: 1 }}
                      >
                        {t('clubs.configure.basicInfo.logo.delete')}
                      </Button>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <input
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setLogoFile(file);
                        setLogoUploaded(false);
                        if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
                        setLogoPreviewUrl(URL.createObjectURL(file));
                      }
                    }}
                  />
                  <label htmlFor="logo-upload">
                    <Button variant="outlined" component="span" startIcon={<Image />}>
                      {t('clubs.configure.basicInfo.logo.selectImage')}
                    </Button>
                  </label>

                  {logoFile && !logoUploaded && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleLogoUpload}
                      disabled={uploadingLogo}
                      startIcon={uploadingLogo ? <CircularProgress size={20} /> : <CloudUpload />}
                    >
                      {uploadingLogo ? t('clubs.configure.basicInfo.logo.uploading') : t('clubs.configure.basicInfo.logo.upload')}
                    </Button>
                  )}

                  {logoUploaded && (
                    <Typography variant="body2" color="success.main">
                      {t('clubs.configure.basicInfo.logo.uploadSuccess')}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveClubDetails}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                >
                  {saving ? t('clubs.configure.basicInfo.saving') : t('clubs.configure.basicInfo.saveChanges')}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                {t('clubs.configure.templates.tabs.cardTemplate')}
              </Typography>
              {process.env.NODE_ENV === 'development' && (
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      const response = await api.get(`/club-templates/${clubId}/list`);
                      console.log('Template Status:', response.data);
                      alert(`Templates in DB:\nFront: ${response.data.card_front_template || 'none'}\nBack: ${response.data.card_back_template || 'none'}\n\nExists in MinIO:\nFront: ${response.data.front_exists}\nBack: ${response.data.back_exists}`);
                    } catch (err) {
                      console.error('Error checking templates:', err);
                    }
                  }}
                >
                  Debug Templates
                </Button>
              )}
            </Box>
            
            {/* Card Creation Instructions */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                📋 {t('clubs.configure.templates.cardImages.workflowTitle')}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>1️⃣ {t('clubs.configure.templates.cardImages.step1')}</strong>
                </Typography>
                <Button 
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUpload />}
                  href="https://www.canva.com/design/DAGZEpZxKA8/OSA7JVWM5wjQrIbRFpwsdQ/edit?utm_content=DAGZEpZxKA8&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mb: 2, ml: 2 }}
                >
                  {t('clubs.configure.templates.cardImages.openCanvaTemplate')}
                </Button>
                
                <Typography variant="body2" paragraph>
                  <strong>2️⃣ {t('clubs.configure.templates.cardImages.step2')}</strong><br />
                  {t('clubs.configure.templates.cardImages.step2desc').split('\n').map((line: string, i: number) => (
                    <span key={i}>{line}<br /></span>
                  ))}
                </Typography>
                
                <Typography variant="body2" paragraph>
                  <strong>3️⃣ {t('clubs.configure.templates.cardImages.step3')}</strong><br />
                  {t('clubs.configure.templates.cardImages.step3desc')}
                </Typography>
                
                <Typography variant="body2" paragraph>
                  <strong>4️⃣ {t('clubs.configure.templates.cardImages.step4')}</strong><br />
                  {t('clubs.configure.templates.cardImages.step4desc')}
                </Typography>
              </Box>
            </Alert>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Card Front Upload */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('clubs.configure.templates.cardImages.front')}
              </Typography>
              
              {currentFrontUrl && !frontPreviewUrl && (
                <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('clubs.configure.templates.cardImages.currentlyUploaded')}
                  </Typography>
                  <img 
                    key={currentFrontUrl} // Force re-render when URL changes
                    src={currentFrontUrl} 
                    alt="Current Front Template"
                    style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }}
                    onError={(e) => {
                      console.error('Failed to load front template image');
                      setCurrentFrontUrl(null);
                    }}
                  />
                </Box>
              )}
              
              {frontPreviewUrl && (
                <Box sx={{ mb: 2, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary" gutterBottom>
                    {t('clubs.configure.templates.cardImages.preview')}
                  </Typography>
                  <img 
                    key={frontPreviewUrl} // Force re-render when URL changes
                    src={frontPreviewUrl} 
                    alt="Front Preview"
                    style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }}
                    onError={(e) => {
                      console.error('Failed to load front preview image');
                      if (frontPreviewUrl) URL.revokeObjectURL(frontPreviewUrl);
                      setFrontPreviewUrl(null);
                    }}
                  />
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <input
                  accept="image/png"
                  style={{ display: 'none' }}
                  id="front-image-upload"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setFrontCardFile(file);
                      setFrontUploaded(false);
                      if (frontPreviewUrl) URL.revokeObjectURL(frontPreviewUrl);
                      setFrontPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                />
                <label htmlFor="front-image-upload">
                  <Button variant="outlined" component="span" startIcon={<Image />}>
                    {t('clubs.configure.templates.actions.selectImage')}
                  </Button>
                </label>
                
                {frontCardFile && !frontUploaded && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleCardUpload('front')}
                    disabled={uploadingFront}
                    startIcon={uploadingFront ? <CircularProgress size={20} /> : <CloudUpload />}
                  >
                    {uploadingFront ? t('clubs.configure.templates.actions.uploading') : t('clubs.configure.templates.actions.upload')}
                  </Button>
                )}
                
                {frontUploaded && (
                  <Typography variant="body2" color="success.main">
                    ✓ {t('clubs.configure.templates.uploadSuccess')}
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Card Back Upload */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('clubs.configure.templates.cardImages.back')}
              </Typography>
              
              {currentBackUrl && !backPreviewUrl && (
                <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('clubs.configure.templates.cardImages.currentlyUploaded')}
                  </Typography>
                  <img 
                    key={currentBackUrl} // Force re-render when URL changes
                    src={currentBackUrl} 
                    alt="Current Back Template"
                    style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }}
                    onError={(e) => {
                      console.error('Failed to load back template image');
                      setCurrentBackUrl(null);
                    }}
                  />
                </Box>
              )}
              
              {backPreviewUrl && (
                <Box sx={{ mb: 2, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary" gutterBottom>
                    {t('clubs.configure.templates.cardImages.preview')}
                  </Typography>
                  <img 
                    key={backPreviewUrl} // Force re-render when URL changes
                    src={backPreviewUrl} 
                    alt="Back Preview"
                    style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }}
                    onError={(e) => {
                      console.error('Failed to load back preview image');
                      if (backPreviewUrl) URL.revokeObjectURL(backPreviewUrl);
                      setBackPreviewUrl(null);
                    }}
                  />
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <input
                  accept="image/png"
                  style={{ display: 'none' }}
                  id="back-image-upload"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setBackCardFile(file);
                      setBackUploaded(false);
                      if (backPreviewUrl) URL.revokeObjectURL(backPreviewUrl);
                      setBackPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                />
                <label htmlFor="back-image-upload">
                  <Button variant="outlined" component="span" startIcon={<Image />}>
                    {t('clubs.configure.templates.actions.selectImage')}
                  </Button>
                </label>
                
                {backCardFile && !backUploaded && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleCardUpload('back')}
                    disabled={uploadingBack}
                    startIcon={uploadingBack ? <CircularProgress size={20} /> : <CloudUpload />}
                  >
                    {uploadingBack ? t('clubs.configure.templates.actions.uploading') : t('clubs.configure.templates.actions.upload')}
                  </Button>
                )}
                
                {backUploaded && (
                  <Typography variant="body2" color="success.main">
                    ✓ {t('clubs.configure.templates.uploadSuccess')}
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Front Position Configuration */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('clubs.configure.templates.cardPosition.title')}
              </Typography>
              <Box>
                <TextField
                  fullWidth
                  value={frontPosition}
                  onChange={(e) => setFrontPosition(e.target.value)}
                  placeholder="80,200"
                  helperText={t('clubs.configure.templates.cardPosition.helper')}
                  sx={{ mb: 2, maxWidth: 400 }}
                />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={handleSaveFrontPosition}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                >
                  {saving ? t('clubs.configure.templates.actions.saving') : t('clubs.configure.templates.actions.savePosition')}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {clubId && <BoardMembersManager clubId={clubId} />}
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            {clubId && <ClubDocumentsManager clubId={clubId} />}
          </TabPanel>
        </Paper>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => {
            setSnackbarOpen(false);
            setSuccess(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setSuccess(null);
            }}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {success}
          </Alert>
        </Snackbar>
      </Container>
    </RequireEmailVerification>
  );
};

export default ConfigureClub;